// src/pages/partner-admin/PartnerUsersPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  getPartnerUsers,
  getPartnerUser,
  banPartnerUser,
  exportPartnerUsersUrl,
} from "@/services/partnerAdminService";
import { usePartnerAdmin } from "@/contexts/PartnerAdminContext";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Avatar({ profile, username, size = 36 }) {
  const src = profile?.avatar_url || profile?.avatar;
  const initials = (profile?.name || username || "?")
    .split(" ")
    .map(w => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  if (src) {
    return (
      <img
        src={src}
        alt={username}
        style={{
          width: size, height: size, borderRadius: "50%",
          objectFit: "cover", flexShrink: 0,
        }}
        onError={e => { e.target.style.display = "none"; }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "#2a2a2a", display: "flex", alignItems: "center",
      justifyContent: "center", fontSize: size * 0.35, color: "#888",
      fontWeight: 600, flexShrink: 0, letterSpacing: "0.02em",
    }}>
      {initials}
    </div>
  );
}

function StatusBadge({ isActive, isBanned }) {
  if (isBanned || !isActive) {
    return (
      <span style={{
        background: "#2a1111", border: "1px solid #4a2222",
        color: "#f08888", fontSize: 10, fontWeight: 600,
        padding: "2px 7px", borderRadius: 4,
        textTransform: "uppercase", letterSpacing: "0.06em",
      }}>Banned</span>
    );
  }
  return (
    <span style={{
      background: "#112a11", border: "1px solid #224a22",
      color: "#88f088", fontSize: 10, fontWeight: 600,
      padding: "2px 7px", borderRadius: 4,
      textTransform: "uppercase", letterSpacing: "0.06em",
    }}>Active</span>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

function UserDetailPanel({ userId, onClose, onBanToggle, isBanAdmin }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [banning, setBanning] = useState(false);

  useEffect(() => {
    setLoading(true);
    getPartnerUser(userId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleBan = async () => {
    if (!data) return;
    const action = data.user.is_active ? "ban" : "unban";
    if (!confirm(`${action === "ban" ? "Ban" : "Unban"} ${data.user.username}?`)) return;
    setBanning(true);
    try {
      await banPartnerUser(userId);
      const updated = await getPartnerUser(userId);
      setData(updated);
      onBanToggle(userId, !data.user.is_active);
    } catch (err) {
      alert(err.message || "Action failed.");
    } finally {
      setBanning(false);
    }
  };

  return (
    <div className="pa-panel-backdrop" onClick={onClose}>
      <div className="pa-panel" onClick={e => e.stopPropagation()}>
        <div className="pa-panel__header">
          <span className="pa-panel__title">User Profile</span>
          <button className="pa-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="pa-panel__body">
          {loading ? (
            <div className="pa-page-loading">Loading...</div>
          ) : !data ? (
            <div className="pa-page-error">Failed to load user.</div>
          ) : (
            <>
              {/* Profile header */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <Avatar profile={data.user.profile} username={data.user.username} size={52} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#e8e8e8" }}>
                    {data.user.profile?.name || data.user.username}
                  </div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                    @{data.user.username}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <StatusBadge isActive={data.user.is_active} isBanned={data.user.is_banned} />
                  </div>
                </div>
              </div>

              {/* Meta */}
              <div className="pa-panel__meta">
                <div className="pa-panel__meta-row">
                  <span className="pa-panel__meta-label">Email</span>
                  <span className="pa-panel__meta-value">{data.user.email || "—"}</span>
                </div>
                <div className="pa-panel__meta-row">
                  <span className="pa-panel__meta-label">Joined</span>
                  <span className="pa-panel__meta-value">
                    {new Date(data.user.date_joined).toLocaleDateString()}
                  </span>
                </div>
                <div className="pa-panel__meta-row">
                  <span className="pa-panel__meta-label">Submissions</span>
                  <span className="pa-panel__meta-value">{data.user.submission_count ?? 0}</span>
                </div>
                {data.user.profile?.bio && (
                  <div className="pa-panel__meta-row" style={{ alignItems: "flex-start" }}>
                    <span className="pa-panel__meta-label">Bio</span>
                    <span className="pa-panel__meta-value" style={{ color: "#888" }}>
                      {data.user.profile.bio}
                    </span>
                  </div>
                )}
              </div>

              {/* Submission history */}
              {data.submissions?.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={{
                    fontSize: 10, color: "#555", textTransform: "uppercase",
                    letterSpacing: "0.07em", marginBottom: 10,
                  }}>
                    Submission History
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {data.submissions.map(sub => (
                      <div key={sub.id} style={{
                        background: "#1a1a1a", border: "1px solid #222",
                        borderRadius: 6, padding: "10px 12px",
                        display: "flex", alignItems: "center", gap: 10,
                      }}>
                        {sub.media_url ? (
                          sub.media_type === "video" ? (
                            <div style={{
                              width: 36, height: 36, borderRadius: 4,
                              background: "#222", display: "flex",
                              alignItems: "center", justifyContent: "center",
                              color: "#555", fontSize: 14, flexShrink: 0,
                            }}>▶</div>
                          ) : (
                            <img src={sub.media_url} alt="" style={{
                              width: 36, height: 36, borderRadius: 4,
                              objectFit: "cover", flexShrink: 0,
                            }} />
                          )
                        ) : (
                          <div style={{
                            width: 36, height: 36, borderRadius: 4,
                            background: "#1e1e1e", flexShrink: 0,
                          }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 12, color: "#aaa", fontWeight: 500,
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          }}>
                            {sub.competition_title || "Competition"}
                          </div>
                          <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                            {new Date(sub.submission_date).toLocaleDateString()}
                          </div>
                        </div>
                        <span style={{
                          fontSize: 10, fontWeight: 600, textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          color: sub.approved ? "#88f088" : "#f0a84a",
                        }}>
                          {sub.approved ? "Approved" : "Pending"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ban button */}
              {isBanAdmin && (
                <div style={{ marginTop: 24 }}>
                  <button
                    className="pa-btn"
                    style={{
                      width: "100%",
                      background: data.user.is_active ? "#2a1111" : "#112a11",
                      border: `1px solid ${data.user.is_active ? "#4a2222" : "#224a22"}`,
                      color: data.user.is_active ? "#f08888" : "#88f088",
                    }}
                    onClick={handleBan}
                    disabled={banning}
                  >
                    {banning
                      ? "Processing..."
                      : data.user.is_active
                        ? `Ban @${data.user.username}`
                        : `Unban @${data.user.username}`
                    }
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PartnerUsersPage() {
  const { membership } = usePartnerAdmin();
  const isAdmin = membership?.is_admin;

  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState("");
  const [filter, setFilter]       = useState("all"); // all | active | banned
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter === "active") params.is_active = "true";
      if (filter === "banned") params.is_active = "false";
      if (search) params.search = search;
      const data = await getPartnerUsers(params);
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const handleBanToggle = (userId, newIsActive) => {
    setUsers(us => us.map(u =>
      u.id === userId ? { ...u, is_active: newIsActive, is_banned: !newIsActive } : u
    ));
  };

  const exportUrl = exportPartnerUsersUrl();

  return (
    <div className="pa-users">
      {/* Toolbar */}
      <div className="pa-toolbar">
        <div className="pa-toolbar__left">
          <input
            className="pa-search"
            placeholder="Search users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="pa-filter-tabs">
            {["all", "active", "banned"].map(f => (
              <button
                key={f}
                className={"pa-filter-tab" + (filter === f ? " pa-filter-tab--active" : "")}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        {isAdmin && (
          <a
            href={exportUrl}
            className="pa-btn pa-btn--ghost"
            style={{ textDecoration: "none", fontSize: 12 }}
          >
            ↓ Export CSV
          </a>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="pa-page-loading">Loading...</div>
      ) : error ? (
        <div className="pa-page-error">{error}</div>
      ) : users.length === 0 ? (
        <div className="pa-empty">
          <span>{search || filter !== "all" ? "No users match your filters." : "No community members yet."}</span>
        </div>
      ) : (
        <div className="pa-table-wrap">
          <table className="pa-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Submissions</th>
                <th>Joined</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr
                  key={user.id}
                  className="pa-table__row"
                  onClick={() => setSelectedId(user.id)}
                >
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar profile={user.profile} username={user.username} size={32} />
                      <div>
                        <div className="pa-table__title">
                          {user.profile?.name || user.username}
                        </div>
                        <div className="pa-table__sub">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="pa-table__sub" style={{ color: "#555" }}>
                    {user.email || "—"}
                  </td>
                  <td className="pa-table__num">{user.submission_count ?? 0}</td>
                  <td className="pa-table__date">
                    {new Date(user.date_joined).toLocaleDateString()}
                  </td>
                  <td>
                    <StatusBadge isActive={user.is_active} isBanned={user.is_banned} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail panel */}
      {selectedId && (
        <UserDetailPanel
          userId={selectedId}
          onClose={() => setSelectedId(null)}
          onBanToggle={handleBanToggle}
          isBanAdmin={isAdmin}
        />
      )}

      <style>{`
        .pa-users { display: flex; flex-direction: column; gap: 20px; }

        .pa-toolbar {
          display: flex; align-items: center;
          justify-content: space-between; gap: 12px; flex-wrap: wrap;
        }
        .pa-toolbar__left { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .pa-search {
          background: #111; border: 1px solid #222; color: #ccc;
          padding: 8px 12px; border-radius: 6px; font-size: 13px;
          width: 220px; outline: none;
        }
        .pa-search:focus { border-color: #333; }
        .pa-filter-tabs { display: flex; gap: 4px; }
        .pa-filter-tab {
          background: none; border: 1px solid #1e1e1e; color: #555;
          font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
          padding: 5px 10px; border-radius: 4px; cursor: pointer;
          transition: border-color 0.15s, color 0.15s;
        }
        .pa-filter-tab:hover { border-color: #333; color: #aaa; }
        .pa-filter-tab--active {
          border-color: var(--colour-primary, #c8f04a);
          color: var(--colour-primary, #c8f04a);
        }

        .pa-table-wrap {
          background: #111; border: 1px solid #1e1e1e;
          border-radius: 8px; overflow: hidden;
        }
        .pa-table { width: 100%; border-collapse: collapse; }
        .pa-table thead tr { border-bottom: 1px solid #1e1e1e; }
        .pa-table th {
          text-align: left; padding: 10px 16px;
          font-size: 10px; color: #444; text-transform: uppercase;
          letter-spacing: 0.07em; font-weight: 600;
        }
        .pa-table__row {
          border-bottom: 1px solid #161616;
          cursor: pointer; transition: background 0.1s;
        }
        .pa-table__row:last-child { border-bottom: none; }
        .pa-table__row:hover { background: #161616; }
        .pa-table td { padding: 12px 16px; vertical-align: middle; }
        .pa-table__title { font-size: 13px; color: #ccc; font-weight: 500; }
        .pa-table__sub   { font-size: 11px; color: #555; margin-top: 2px; }
        .pa-table__num   { font-size: 13px; color: #666; text-align: center; }
        .pa-table__date  { font-size: 11px; color: #555; white-space: nowrap; }

        /* Detail panel */
        .pa-panel-backdrop {
          position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 1000; display: flex; justify-content: flex-end;
        }
        .pa-panel {
          width: 360px; max-width: 90vw; height: 100%;
          background: #141414; border-left: 1px solid #222;
          display: flex; flex-direction: column; overflow: hidden;
          animation: slideIn 0.2s ease;
        }
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        .pa-panel__header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 18px 20px; border-bottom: 1px solid #1e1e1e;
          flex-shrink: 0;
        }
        .pa-panel__title { font-size: 13px; font-weight: 600; color: #e8e8e8; }
        .pa-panel__body {
          flex: 1; overflow-y: auto; padding: 20px;
        }
        .pa-panel__meta {
          background: #1a1a1a; border: 1px solid #222;
          border-radius: 6px; overflow: hidden;
        }
        .pa-panel__meta-row {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 14px; border-bottom: 1px solid #1e1e1e;
        }
        .pa-panel__meta-row:last-child { border-bottom: none; }
        .pa-panel__meta-label {
          font-size: 11px; color: #555; text-transform: uppercase;
          letter-spacing: 0.06em; width: 80px; flex-shrink: 0;
        }
        .pa-panel__meta-value { font-size: 12px; color: #aaa; }

        /* Shared */
        .pa-modal__close {
          background: none; border: none; color: #555; font-size: 14px;
          cursor: pointer; padding: 4px 8px;
        }
        .pa-modal__close:hover { color: #aaa; }
        .pa-btn {
          padding: 9px 18px; border-radius: 6px; font-size: 13px;
          cursor: pointer; border: none; font-weight: 500; transition: opacity 0.15s;
          font-family: inherit;
        }
        .pa-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .pa-btn--ghost {
          background: none; border: 1px solid #2a2a2a; color: #888;
        }
        .pa-btn--ghost:hover { border-color: #444; color: #ccc; }
        .pa-empty {
          background: #111; border: 1px dashed #222; border-radius: 8px;
          padding: 40px; display: flex; align-items: center; justify-content: center;
          color: #444; font-size: 13px;
        }
        .pa-page-loading { color: #555; font-size: 13px; padding: 40px 0; }
        .pa-page-error   { color: #f04a4a; font-size: 13px; padding: 40px 0; }
      `}</style>
    </div>
  );
}